import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Building2, Network, Loader2, CheckSquare, Square, BarChart3,
  TrendingUp, TrendingDown, Percent, Layers,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";

const fmtEur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const fmtEurFull = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);

export default function ConsolidatedAccountingPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();

  const [societes, setSocietes] = useState([]);
  const [selected, setSelected] = useState([]); // societe_ids checked
  const [fiscalYears, setFiscalYears] = useState([]); // distinct codes across societes
  const [fyCode, setFyCode] = useState("");
  const [view, setView] = useState("balance"); // balance | cr
  const [balance, setBalance] = useState(null);
  const [cr, setCr] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load societes + fiscal years
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/societes");
        const list = r.data || [];
        setSocietes(list);
        setSelected(list.map(s => s.societe_id)); // all selected by default
        // Fetch FY codes across societes (distinct)
        const codes = new Set();
        for (const s of list) {
          try {
            const ry = await api.get(`/accounting/fiscal-years?societe_id=${s.societe_id}`);
            (ry.data || []).forEach(fy => fy.code && codes.add(fy.code));
          } catch (_) {}
        }
        const arr = [...codes].sort().reverse();
        setFiscalYears(arr);
        if (arr[0]) setFyCode(arr[0]);
      } catch (e) {
        toast.error("Impossible de charger les sociétés");
      }
    })();
    // eslint-disable-next-line
  }, []);

  const toggle = (sid) => {
    setSelected(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };
  const toggleAll = () => {
    setSelected(prev => prev.length === societes.length ? [] : societes.map(s => s.societe_id));
  };

  const reload = async () => {
    if (!selected.length) {
      toast.warning("Sélectionnez au moins une société");
      return;
    }
    setLoading(true);
    const sidsParam = selected.join(",");
    const fyParam = fyCode ? `&fiscal_year_code=${encodeURIComponent(fyCode)}` : "";
    try {
      const [rb, rc] = await Promise.all([
        api.get(`/accounting/reports/consolidated/balance?societe_ids=${sidsParam}${fyParam}`),
        api.get(`/accounting/reports/consolidated/compte-resultat?societe_ids=${sidsParam}${fyParam}`),
      ]);
      setBalance(rb.data);
      setCr(rc.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du chargement");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (societes.length && selected.length) reload();
    // eslint-disable-next-line
  }, [fyCode]);

  const balanceClasses = useMemo(() => {
    if (!balance?.rows) return [];
    const groups = {};
    for (const r of balance.rows) {
      const c = String(r.account_number || "")[0] || "?";
      if (!groups[c]) groups[c] = { classe: c, debit: 0, credit: 0, count: 0 };
      groups[c].debit += r.debit;
      groups[c].credit += r.credit;
      groups[c].count += 1;
    }
    return Object.values(groups).sort((a, b) => a.classe.localeCompare(b.classe));
  }, [balance]);

  const result = cr?.result || 0;
  const margePct = cr?.total_produits ? (result / cr.total_produits * 100) : 0;

  return (
    <div className="p-6 space-y-6 min-h-screen" style={{ background: "var(--bg-primary)" }} data-testid="consolidated-accounting-page">
      {/* Header */}
      <PageHeader
        icon={<Network />}
        title="Consolidation comptable"
        description="Vue holding : addition des écritures de toutes les sociétés sélectionnées sur l'exercice choisi."
        breadcrumb={[{ label: "Comptabilité" }, { label: "Vue groupe" }]}
        testId="consolidated-header"
        actions={(
          <>
            {fiscalYears.length > 0 && (
              <Select value={fyCode || "__none__"} onValueChange={v => setFyCode(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="consolidated-fy-select" className="h-9 w-auto min-w-[160px] bg-transparent border text-xs" style={{ borderColor: "var(--border-primary)", color: "var(--text-primary)" }}>
                  <SelectValue placeholder="Tous exercices" />
                </SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white max-h-72">
                  <SelectItem value="__none__" className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid="consolidated-fy-option-all">Tous exercices</SelectItem>
                  {fiscalYears.map(c => (
                    <SelectItem key={c} value={c} className="text-xs text-slate-200 focus:bg-white/10 focus:text-white" data-testid={`consolidated-fy-option-${c}`}>Exercice {c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button onClick={reload} disabled={loading || !selected.length} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-9" data-testid="consolidated-refresh-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <BarChart3 className="w-3.5 h-3.5 mr-1.5" />}
              Recalculer
            </Button>
          </>
        )}
      />

      {/* Société picker */}
      <div className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid="consolidated-societe-picker">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" style={{ color: "#f7c935" }} />
            <h3 className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>Périmètre de consolidation</h3>
            <span className="text-xs px-2 py-0.5 rounded bg-[#f7c935]/10 text-[#f7c935] font-bold" data-testid="consolidated-count-badge">
              {selected.length} / {societes.length}
            </span>
          </div>
          <button onClick={toggleAll} className="text-xs text-slate-400 hover:text-white" data-testid="consolidated-toggle-all-btn">
            {selected.length === societes.length ? "Tout désélectionner" : "Tout sélectionner"}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {societes.map(s => {
            const checked = selected.includes(s.societe_id);
            return (
              <button
                key={s.societe_id}
                onClick={() => toggle(s.societe_id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${checked ? "border-[#f7c935]/50 bg-[#f7c935]/5" : "border-white/10 hover:border-white/20"}`}
                style={{ background: checked ? undefined : "var(--bg-input)" }}
                data-testid={`consolidated-societe-${s.societe_id}`}
              >
                {checked ? <CheckSquare className="w-4 h-4 text-[#f7c935] flex-shrink-0" /> : <Square className="w-4 h-4 text-slate-500 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate" style={{ color: "var(--text-primary)" }}>{s.name}</div>
                  {s.siren && <div className="text-[9px] text-slate-500">SIREN {s.siren}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* KPIs groupe */}
      {cr && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="consolidated-kpis">
          <KpiCard icon={TrendingUp} label="Produits groupe" value={fmtEur(cr.total_produits)} color="#10b981" testId="kpi-total-produits" />
          <KpiCard icon={TrendingDown} label="Charges groupe" value={fmtEur(cr.total_charges)} color="#ef4444" testId="kpi-total-charges" />
          <KpiCard icon={Layers} label={result >= 0 ? "Résultat (bénéf.)" : "Résultat (perte)"} value={fmtEur(result)} color={result >= 0 ? "#10b981" : "#ef4444"} testId="kpi-result" />
          <KpiCard icon={Percent} label="Marge nette" value={`${margePct.toFixed(1)} %`} color="#f7c935" testId="kpi-marge" />
        </div>
      )}

      {/* View toggle */}
      <div className="flex items-center gap-1 rounded-lg border px-1 py-1 w-fit" style={{ borderColor: "var(--border-primary)" }}>
        {[{ k: "balance", l: "Balance générale" }, { k: "cr", l: "Compte de résultat" }].map(v => (
          <button key={v.k} onClick={() => setView(v.k)} data-testid={`consolidated-view-${v.k}`}
            className={`px-4 py-1.5 rounded-md text-[11px] font-bold transition-colors ${view === v.k ? "text-[#0b0811]" : ""}`}
            style={view === v.k ? { background: "#f7c935" } : { color: "var(--text-muted)" }}>
            {v.l}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>
      ) : view === "balance" ? (
        <BalanceView balance={balance} balanceClasses={balanceClasses} />
      ) : (
        <CompteResultatView cr={cr} />
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="rounded-xl border p-4" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>{label}</span>
      </div>
      <div className="text-xl font-black tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function BalanceView({ balance, balanceClasses }) {
  if (!balance) return <div className="text-xs text-slate-500 py-10 text-center" data-testid="consolidated-empty">Sélectionnez des sociétés et cliquez sur Recalculer.</div>;
  const CLASS_LABELS = { "1": "Capitaux", "2": "Immobilisations", "3": "Stocks", "4": "Tiers", "5": "Financiers", "6": "Charges", "7": "Produits", "8": "Spéciaux" };
  return (
    <div className="space-y-4">
      {/* Class summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="consolidated-class-summary">
        {balanceClasses.map(c => (
          <div key={c.classe} className="rounded-lg border p-3" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid={`consolidated-class-${c.classe}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>Classe {c.classe}</div>
            <div className="text-xs font-semibold mb-1" style={{ color: "var(--text-primary)" }}>{CLASS_LABELS[c.classe] || ""}</div>
            <div className="text-sm font-black tabular-nums" style={{ color: "#f7c935" }}>{fmtEur(c.debit - c.credit)}</div>
            <div className="text-[10px] text-slate-500">{c.count} comptes</div>
          </div>
        ))}
      </div>

      {/* Detail table */}
      <div className="rounded-xl border overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>Balance détaillée — {balance.rows.length} comptes</h3>
          <div className="flex items-center gap-3 text-xs">
            <span><span className="text-slate-500">Total débit : </span><span className="font-bold tabular-nums" style={{ color: "var(--text-primary)" }}>{fmtEurFull(balance.total_debit)}</span></span>
            <span><span className="text-slate-500">Total crédit : </span><span className="font-bold tabular-nums" style={{ color: "var(--text-primary)" }}>{fmtEurFull(balance.total_credit)}</span></span>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs" data-testid="consolidated-balance-table">
            <thead className="sticky top-0" style={{ background: "var(--bg-card)" }}>
              <tr className="text-left border-b border-white/5">
                <th className="px-4 py-2 font-bold" style={{ color: "var(--text-muted)" }}>N° compte</th>
                <th className="px-4 py-2 font-bold" style={{ color: "var(--text-muted)" }}>Libellé</th>
                <th className="px-4 py-2 font-bold text-right" style={{ color: "var(--text-muted)" }}>Débit</th>
                <th className="px-4 py-2 font-bold text-right" style={{ color: "var(--text-muted)" }}>Crédit</th>
                <th className="px-4 py-2 font-bold text-right" style={{ color: "var(--text-muted)" }}>Solde</th>
              </tr>
            </thead>
            <tbody>
              {balance.rows.map(r => (
                <tr key={r.account_number} className="border-b border-white/5 hover:bg-white/5" data-testid={`balance-row-${r.account_number}`}>
                  <td className="px-4 py-2 font-mono font-bold" style={{ color: "#f7c935" }}>{r.account_number}</td>
                  <td className="px-4 py-2" style={{ color: "var(--text-primary)" }}>{r.label}</td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums" style={{ color: "var(--text-primary)" }}>{r.debit ? fmtEurFull(r.debit) : "—"}</td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums" style={{ color: "var(--text-primary)" }}>{r.credit ? fmtEurFull(r.credit) : "—"}</td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums font-bold" style={{ color: r.balance >= 0 ? "#10b981" : "#ef4444" }}>{fmtEurFull(r.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CompteResultatView({ cr }) {
  if (!cr) return <div className="text-xs text-slate-500 py-10 text-center">Sélectionnez des sociétés et cliquez sur Recalculer.</div>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="consolidated-cr-view">
      <Section title="Produits" total={cr.total_produits} color="#10b981" rows={cr.produits} testId="cr-produits" />
      <Section title="Charges" total={cr.total_charges} color="#ef4444" rows={cr.charges} testId="cr-charges" />
      <div className="lg:col-span-2 rounded-xl border p-4 flex items-center justify-between" style={{ background: cr.result >= 0 ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)", borderColor: cr.result >= 0 ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)" }} data-testid="cr-result-banner">
        <div className="flex items-center gap-3">
          <Layers className="w-5 h-5" style={{ color: cr.result >= 0 ? "#10b981" : "#ef4444" }} />
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-muted)" }}>Résultat consolidé groupe</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>Sur {cr.societe_count} société{cr.societe_count > 1 ? "s" : ""}</div>
          </div>
        </div>
        <div className="text-2xl font-black tabular-nums" style={{ color: cr.result >= 0 ? "#10b981" : "#ef4444" }}>{fmtEurFull(cr.result)}</div>
      </div>
    </div>
  );
}

function Section({ title, total, color, rows, testId }) {
  return (
    <div className="rounded-xl border overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} data-testid={testId}>
      <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
        <h3 className="text-sm font-bold" style={{ color }}>{title}</h3>
        <span className="text-sm font-black tabular-nums" style={{ color }}>{fmtEurFull(total)}</span>
      </div>
      <div className="max-h-[50vh] overflow-y-auto">
        {!rows.length ? (
          <div className="text-xs text-slate-500 py-6 text-center">Aucune ligne</div>
        ) : (
          <table className="w-full text-xs">
            <tbody>
              {rows.map(r => (
                <tr key={r.account_number} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2 font-mono font-bold w-[80px]" style={{ color: "#f7c935" }}>{r.account_number}</td>
                  <td className="px-4 py-2" style={{ color: "var(--text-primary)" }}>{r.label}</td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums font-bold" style={{ color }}>{fmtEurFull(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
